import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { Box, Text, Avatar, Tooltip, useMantineColorScheme, Group } from '@mantine/core';
import { ScimAuthService } from '../services/ScimAuthService';
import MeService from '../services/MeService';
import ImpersonationService from '../services/ImpersonationService';
import { useIsImperator } from '../hooks/useIsImperator';
import { EntitiesMeDialog } from './EntitiesMeButton';
import { getEntityIconAndColor } from '../utils/navigationIconUtils';

function getEntityTypeIcon(entityType: string | undefined): { icon: React.ReactElement; color: string } {
    const { Icon, color } = getEntityIconAndColor(entityType || 'Person');
    return { icon: <Icon />, color };
}

export const UserInfo: React.FC = () => {
    const { accounts } = useMsal();
    const { colorScheme } = useMantineColorScheme();
    const [directTokenAccount, setDirectTokenAccount] = useState<any>(null);
    const [meData, setMeData] = useState<ReturnType<typeof MeService.getMe>>(MeService.getMe());
    const [meDialogOpen, setMeDialogOpen] = useState(false);
    const isImperator = useIsImperator();
    const [onBehalfOfActive, setOnBehalfOfActive] = useState(() => ImpersonationService.getIsOnBehalfOfEffective());

    useEffect(() => {
        const account = ScimAuthService.getCurrentAccount();
        if (account && account.environment === 'direct-token') {
            setDirectTokenAccount(account);
        }
    }, []);

    useEffect(() => {
        const update = () => setMeData(MeService.getMe());
        update();
        const unsub = MeService.subscribe(update);
        return unsub;
    }, []);

    useEffect(() => {
        const sync = () => setOnBehalfOfActive(ImpersonationService.getIsOnBehalfOfEffective());
        const unsub = ImpersonationService.subscribeOnBehalfOf(sync);
        return unsub;
    }, []);

    const msalAccount = accounts.length > 0 ? accounts[0] : null;
    const account = directTokenAccount || msalAccount;

    if (!account) {
        return null;
    }

    const entityTypeRaw = meData?.EntityType;
    const entityTypeStr = typeof entityTypeRaw === 'string'
        ? entityTypeRaw
        : (entityTypeRaw as any)?.DisplayName ?? (entityTypeRaw as any)?.Id ?? 'Person';
    const { icon, color } = getEntityTypeIcon(entityTypeStr);

    const imperatorBorder = isImperator === true ? '2px solid #ffd700' : undefined;

    const displayName = onBehalfOfActive && (meData?.DisplayName ?? meData?.displayName)
        ? (meData.DisplayName ?? meData.displayName)
        : (account.name || account.username);
    const displayUsername = onBehalfOfActive && (meData?.UserPrincipalName ?? meData?.userPrincipalName ?? meData?.Username ?? meData?.username)
        ? (meData.UserPrincipalName ?? meData.userPrincipalName ?? meData.Username ?? meData.username)
        : account.username;

    const tooltipLabel = (
        <Box>
            <Text size="sm"><b>User type:</b> {entityTypeStr}</Text>
            <Text size="sm">
                {isImperator === true ? <><b>Imperator:</b> Yes</> : <><b>Admin:</b> No</>}
            </Text>
            {onBehalfOfActive && (
                <Text size="sm" c="yellow">Viewing as effective user (on-behalf-of).</Text>
            )}
            <Text size="sm" mt={4}>View current user (entities/me). Click for more info.</Text>
        </Box>
    );

    return (
        <>
            <Tooltip label={tooltipLabel} multiline w={280}>
                <Group
                    gap="sm"
                    align="center"
                    style={{
                        cursor: 'pointer',
                        borderRadius: 'var(--mantine-radius-sm)',
                        padding: '2px 6px',
                        ...(onBehalfOfActive && {
                            outline: '2px solid var(--mantine-color-yellow-5)',
                            outlineOffset: 2,
                        }),
                    }}
                    onClick={() => setMeDialogOpen(true)}
                >
                    <Avatar
                        color={color}
                        radius="xl"
                        style={{
                            border: imperatorBorder,
                        }}
                    >
                        {icon}
                    </Avatar>
                    <Box>
                        <Text size="sm" fw={700} lh={1.2}>{displayName}</Text>
                        <Text size="xs" c="dimmed" lh={1.2}>{displayUsername}</Text>
                    </Box>
                </Group>
            </Tooltip>
            <EntitiesMeDialog open={meDialogOpen} onClose={() => setMeDialogOpen(false)} />
        </>
    );
};
